import React, { useCallback, useContext, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import { useNavigate } from "react-router-dom";
import { fetchNumFollowTypeFollowers } from "../../../../utils/userUtils";
import { API } from "../../../../API";
import ConfirmActionModal from "../../../Containers/ConfirmPopups/ConfirmActionModal";
import AlertContainer from "../../../Containers/AlertContainer";
import { Icon } from "@iconify/react";
import { Colors } from "../../../../utils/colors";
import RectangleButton from "../../../Buttons/RectangleButton";
import IntegrationPanelContainer from "./IntegrationPanelContainer";
import {
  GetDateWithTime,
  GetTime,
  hasSubscriptionUnlockedAdvancedData,
} from "@markit/common.utils";
import { GetNumericDate } from "../../../../utils/GetTime";
import { CircularProgress } from "@mui/material";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import EventbriteIcon from "../../../IntegrationIcons/EventbriteIcon";
import {
  checkLastIntegrationSyncDate,
  disconnectEventbrite,
  EventbriteApiLimitAlert,
} from "../../../../utils/eventbriteUtils";
import { MixpanelContext } from "../../../../context/AnalyticsService";
import useAsyncOnMount from "../../../../hooks/useAsyncEffectOnMount";
import { showNotificationBanner } from "../../../../utils/notificationUtils";
import { PlanTypeV4, FollowType, NotificationType } from "@markit/common.types";
import { notificationActions } from "../../../../redux/slices/notificationSlice";

enum EventbriteConfirmType {
  SYNC = "sync",
  DISCONNECT = "disconnect",
  NONE = "none",
}

type EventbritePanelSectionProps = {};

export const EventbritePanelSection = (props: EventbritePanelSectionProps) => {
  const { accountData } = useSelector(getAccountState).account;
  const mixpanel = useContext(MixpanelContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [numEventbriteContacts, setNumEventbriteContacts] = useState(-1);
  const [loadingSync, setLoadingSync] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState<EventbriteConfirmType>(
    EventbriteConfirmType.NONE
  );
  const [alertText, setAlertText] = useState<{
    header: string;
    subHeader: string;
  }>({
    header: "",
    subHeader: "",
  });

  const eventbriteDescription =
    "Seamlessly connect all your attendees and contacts to sync with your Markit contacts and start texting.";

  useAsyncOnMount(async () => {
    // Gets the number of eventbrite followers
    const numEventbriteContacts = await fetchNumFollowTypeFollowers(
      accountData.uid,
      FollowType.EVENTBRITE
    );
    setNumEventbriteContacts(numEventbriteContacts);
  });

  const eventbriteSyncedTimestamp = useMemo(
    () =>
      accountData.eventbriteInfo.syncedOn ||
      accountData.eventbriteInfo.connectedOn,
    [
      accountData.eventbriteInfo.connectedOn,
      accountData.eventbriteInfo.syncedOn,
    ]
  );

  // So that admins can test this without being blocked by no subscription
  const hasUnlockedAdvancedDataOrAdmin = useMemo(
    () =>
      hasSubscriptionUnlockedAdvancedData(accountData) || accountData.isAdmin,
    [accountData]
  );

  const connectEventbriteOnPress = useCallback(async () => {
    if (!hasUnlockedAdvancedDataOrAdmin) {
      dispatch(notificationActions.setMarkitPlusModal(PlanTypeV4.PRO.name));
      return;
    }
    const response = await API.eventScraper.authorizeEventbriteUser({
      userId: accountData.uid,
    });
    if (response) {
      window.open(response.url);
    }
  }, [accountData, dispatch, hasUnlockedAdvancedDataOrAdmin]);

  const syncEventbriteOnPress = useCallback(async () => {
    const disableSync = checkLastIntegrationSyncDate(
      accountData.eventbriteInfo.syncedOn,
      accountData.isAdmin,
      1440
    );
    if (disableSync) {
      const oneDayLater = new Date(accountData.eventbriteInfo.syncedOn);
      oneDayLater.setDate(oneDayLater.getDate() + 1);
      setAlertText({
        header: "You have synced your Eventbrite data within the last day.",
        subHeader: `Please wait until ${GetDateWithTime(
          oneDayLater.toISOString(),
          true
        )} before syncing again.`,
      });
      return;
    }
    setLoadingSync(true);
    const { success, rateLimits } =
      await API.eventScraper.connectEventbriteData({
        eventbriteInfo: accountData.eventbriteInfo,
        userId: accountData.uid,
        isResync: true,
      });
    if (!success) {
      if (rateLimits) {
        setAlertText({
          header: "Cannot connect to Eventbrite at this time.",
          subHeader: EventbriteApiLimitAlert,
        });
        return;
      }
      setConfirmModal(EventbriteConfirmType.SYNC);
    } else {
      showNotificationBanner(dispatch, "Synced!", NotificationType.AFFIRMATIVE);
    }
    setLoadingSync(false);
  }, [
    accountData.eventbriteInfo,
    accountData.isAdmin,
    accountData.uid,
    dispatch,
  ]);

  const disconnectEventbriteOnPress = useCallback(async () => {
    dispatch(disconnectEventbrite(accountData, mixpanel));
  }, [accountData, dispatch, mixpanel]);

  const editConnectionOnPress = useCallback(() => {
    navigate("/integrations");
  }, [navigate]);

  return (
    <>
      <IntegrationPanelContainer
        isConnected={!!accountData.eventbriteInfo.token}
        connectedContent={
          <div
            className="ColumnNormal"
            style={{ height: "100%", width: "100%" }}
          >
            <div className="AlignedRow" style={{ gap: 14, padding: 14 }}>
              <EventbriteIcon size={34} padding={10} />
              <div className="ColumnNormal" style={{ gap: 7 }}>
                <div className="AlignedRow" style={{ gap: 10 }}>
                  <span className="sectionTitle">Eventbrite</span>
                  <span className="bodySubtext">Connected</span>
                </div>
                <span className="smallBodySubtext" style={{ width: "70%" }}>
                  {eventbriteDescription}
                </span>
              </div>
            </div>
            <HorizontalDivider />
            <div
              className="ColumnNormal"
              style={{
                padding: 14,
                flexGrow: 1,
                justifyContent: "space-between",
              }}
            >
              <div className="ColumnNormal" style={{ gap: 10 }}>
                {numEventbriteContacts !== -1 ? (
                  <span
                    className="smallBodySubtext"
                    style={{
                      color:
                        numEventbriteContacts > 0 ? Colors.GRAY1 : Colors.RED3,
                    }}
                  >
                    Total Valid Synced Contacts: {numEventbriteContacts}
                  </span>
                ) : null}
                {accountData.eventbriteInfo.autoAddPhoneQuestion ? (
                  <div className="AlignedRow" style={{ gap: 4 }}>
                    <Icon
                      icon={"ion:checkmark-circle"}
                      height={12}
                      style={{ color: Colors.GREEN2 }}
                    />
                    <span style={{ fontSize: 12 }}>
                      Auto Collecting Phone Numbers
                    </span>
                  </div>
                ) : null}
                <span className="smallBodySubtext">
                  Connected On:{" "}
                  {GetNumericDate(accountData.eventbriteInfo.connectedOn)}
                </span>
              </div>
              <div
                className="AlignedRow"
                style={{ alignContent: "flex-end", gap: 14 }}
              >
                <div className="AlignedRow" style={{ gap: 10 }}>
                  <RectangleButton
                    buttonLabel={
                      <span style={{ fontSize: 14, fontWeight: 400 }}>
                        Disconnect
                      </span>
                    }
                    onPress={() =>
                      setConfirmModal(EventbriteConfirmType.DISCONNECT)
                    }
                    altColor={Colors.WHITE1}
                    altTextColor={Colors.RED3}
                    altPaddingHorz={14}
                    altPaddingVert={10}
                    altBorderRadius={8}
                    width={106}
                    containerStyles={{ border: "0.5px solid #EDEDED" }}
                  />
                  <div>
                    <RectangleButton
                      buttonLabel={
                        <span style={{ fontSize: 14, fontWeight: 400 }}>
                          Edit Connection
                        </span>
                      }
                      onPress={editConnectionOnPress}
                      altColor={Colors.WHITE1}
                      altTextColor={Colors.BLACK}
                      altPaddingHorz={14}
                      altPaddingVert={10}
                      altBorderRadius={8}
                      containerStyles={{ border: "0.5px solid #EDEDED" }}
                    />
                  </div>
                  {accountData.isAdmin &&
                  accountData.eventbriteInfo.syncedOn ? (
                    <div
                      className="AlignedRowSelect"
                      style={{
                        paddingInline: 9,
                        paddingBlock: 10,
                        gap: 10,
                        pointerEvents: loadingSync ? "none" : "all",
                      }}
                      onClick={syncEventbriteOnPress}
                    >
                      {loadingSync ? (
                        <CircularProgress
                          style={{
                            color: Colors.BLACK,
                            alignSelf: "center",
                            paddingInline: 2,
                          }}
                          size={20}
                        />
                      ) : (
                        <Icon icon="ion:sync-sharp" height={24} />
                      )}
                      <div className="ColumnNormalSelect">
                        <span style={{ fontSize: 12, fontWeight: 500 }}>
                          Refresh Data (admin only for now)
                        </span>
                        <span style={{ fontSize: 11, color: Colors.GRAY1 }}>
                          Last Synced:{" "}
                          {GetNumericDate(eventbriteSyncedTimestamp)}{" "}
                          {GetTime(eventbriteSyncedTimestamp)}
                        </span>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        }
        title="Eventbrite Integration"
        description={eventbriteDescription}
        actionLabel={hasUnlockedAdvancedDataOrAdmin ? "Connect" : "Upgrade"}
        onActionPress={
          hasUnlockedAdvancedDataOrAdmin
            ? () => setShowConnectModal(true)
            : connectEventbriteOnPress
        }
        icon={<EventbriteIcon size={57} padding={22} borderRadius={15} />}
      />
      <ConfirmActionModal
        heading="Connect to Eventbrite"
        subtext={
          <span>
            Before pressing connect, make sure you{" "}
            <span
              onClick={connectEventbriteOnPress}
              style={{
                cursor: "pointer",
                color: Colors.BLUE5,
                textDecorationLine: "underline",
              }}
            >
              login
            </span>{" "}
            to your Eventbrite account first.
          </span>
        }
        confirmButtonText="Connect"
        icon={
          <Icon
            icon="simple-icons:eventbrite"
            height={35}
            style={{ color: Colors.ORANGE_EB }}
          />
        }
        hideModal={!showConnectModal}
        setIsVisible={setShowConnectModal}
        confirmOnPress={() => {
          setShowConnectModal(false);
          connectEventbriteOnPress();
        }}
      />
      <ConfirmActionModal
        heading={
          confirmModal === EventbriteConfirmType.SYNC
            ? "Unable to re-sync with Eventbrite at this time."
            : "Are you sure you want to disconnect your Eventbrite integration with Markit?"
        }
        subtext={
          confirmModal === EventbriteConfirmType.SYNC
            ? "We weren't able to sync your latest Eventbrite data. Please try again later or contact support."
            : "This cannot be undone."
        }
        confirmButtonText={
          confirmModal === EventbriteConfirmType.SYNC
            ? "Try Again"
            : "Disconnect"
        }
        icon={
          confirmModal === EventbriteConfirmType.SYNC ? (
            <Icon icon="fa-solid:plug" height={35} />
          ) : undefined
        }
        hideModal={confirmModal === EventbriteConfirmType.NONE}
        setIsVisible={() => setConfirmModal(EventbriteConfirmType.NONE)}
        confirmOnPress={
          confirmModal === EventbriteConfirmType.SYNC
            ? syncEventbriteOnPress
            : disconnectEventbriteOnPress
        }
      />
      <AlertContainer
        headerComp={alertText.header}
        subHeaderComp={alertText.subHeader}
        closeModal={() => setAlertText({ header: "", subHeader: "" })}
        hideModal={alertText.header === ""}
      />
    </>
  );
};
